import api from './api';

export interface DocumentUploadResponse {
  job_id: string;
  message: string;
  status_url?: string;
}

export interface DocumentStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message?: string;
  created_at: string;
  updated_at: string;
}

export interface ValidationResult {
  rule_id: string;
  passed: boolean;
  severity: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
}

export interface ExtractedData {
  patient?: {
    name?: { value: string; confidence: number };
    mrn?: { value: string; confidence: number };
    dob?: { value: string; confidence: number };
  };
  provider?: {
    name?: { value: string; confidence: number };
    npi?: { value: string; confidence: number };
  };
  date_of_service?: { value: string; confidence: number };
  [key: string]: any;
}

export interface DocumentResult {
  job_id: string;
  document_type: string;
  confidence_score: number;
  extracted_data: ExtractedData;
  validation_results: ValidationResult[];
  requires_manual_review: boolean;
  metadata: any;
  processing_time: number;
}

export interface DocumentListItem {
  id: string;
  filename?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  document_type?: string;
  confidence_score?: number;
  created_at: string;
  updated_at: string;
}

class DocumentService {
  // Upload document
  async uploadDocument(file: File): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(
      '/api/v1/documents/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    
    const data = response.data;
    return {
      job_id: data.job_id || data.id || data.document_id,
      message: data.message || 'Upload successful',
      status_url: data.status_url
    };
  }

  // Get document status
  async getDocumentStatus(jobId: string): Promise<DocumentStatus> {
    const response = await api.get<DocumentStatus>(
      `/api/v1/documents/${jobId}/status`
    );
    return response.data;
  }

  // Get document results
  async getDocumentResults(jobId: string): Promise<DocumentResult> {
    const response = await api.get<DocumentResult>(
      `/api/v1/documents/${jobId}/results`
    );
    return response.data;
  }

  // List documents
  async listDocuments(
    skip: number = 0,
    limit: number = 20,
    status?: string
  ): Promise<DocumentListItem[]> {
    const params: any = { skip, limit };
    if (status) params.status = status;

    const response = await api.get<DocumentListItem[]>(
      '/api/v1/documents/',
      { params }
    );
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response = await api.get('/health');
    return response.data;
  }
}

export default new DocumentService();